import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, X, Copy, Check, Share2, Wand2, LogIn } from "lucide-react";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";
import { bakeDisclaimerOnBase64 } from "../../utils/aiDisclaimer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SmartInviteModal — AI-generated invite creator + share UI.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   defaults: { occasion, package, booking_id, magic_job_id, host_name, event_date }
 *   onCreated?: (invite) => void
 */
export default function SmartInviteModal({ open, onClose, defaults = {}, onCreated }) {
  const [step, setStep] = useState(1); // 1=form, 2=result
  const [style, setStyle] = useState("surprise"); // surprise | reveal
  const [hostName, setHostName] = useState(defaults.host_name || "");
  const [eventDate, setEventDate] = useState(defaults.event_date || "");
  const [eventTime, setEventTime] = useState(defaults.event_time || "");
  const [venue, setVenue] = useState(defaults.venue || "");
  const [note, setNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Sync form state when `defaults` changes (e.g. user opens modal for a different booking)
  useEffect(() => {
    if (!open) return;
    if (defaults.host_name !== undefined) setHostName(defaults.host_name || "");
    if (defaults.event_date !== undefined) setEventDate(defaults.event_date || "");
    if (defaults.event_time !== undefined) setEventTime(defaults.event_time || "");
    if (defaults.venue !== undefined) setVenue(defaults.venue || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaults.booking_id, defaults.magic_job_id]);

  const canReveal = Boolean(defaults.magic_job_id || defaults.booking_id);

  if (!open) return null;

  const reset = () => {
    setStep(1); setResult(null); setError(""); setCopied(false);
  };
  const close = () => { reset(); onClose(); };

  const generate = async () => {
    if (!eventDate) { setError("Please pick an event date"); return; }
    setError(""); setGenerating(true);
    try {
      const r = await fetch(`${API_URL}/api/smart-invites/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          booking_id: defaults.booking_id || null,
          magic_job_id: defaults.magic_job_id || null,
          occasion: defaults.occasion || "Birthday",
          package: defaults.package || "",
          event_date: eventDate,
          event_time: eventTime,
          venue,
          host_name: hostName,
          personal_note: note,
          style,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        if (r.status === 401) {
          throw new Error("Please sign in to your DoozyGo account first — tap 'My Account' in the menu.");
        }
        if (r.status === 502 || r.status === 504) {
          throw new Error("Our AI is busy right now. Please try again in a few seconds.");
        }
        throw new Error(d.detail || `Couldn't create invite (${r.status})`);
      }
      const data = await r.json();
      setResult(data);
      setStep(2);
      if (onCreated) onCreated(data);
    } catch (e) { setError(typeof e?.message === "string" ? e.message : "Couldn't create invite"); }
    setGenerating(false);
  };

  const publicUrl = result ? `${window.location.origin}/rsvp/${result.invite_id}` : "";

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const nativeShare = async () => {
    const text = `You're invited to ${result.host_name}'s ${result.occasion}! RSVP here: ${publicUrl}`;
    if (navigator.share) {
      try {
        const dataUrl = await bakeDisclaimerOnBase64(result.image_base64, "image/jpeg");
        const blob = await fetch(dataUrl).then(r => r.blob());
        const file = new File([blob], `invite-${result.invite_id}.jpg`, { type: "image/jpeg" });
        try {
          await navigator.share({ text, url: publicUrl, files: [file] });
          return;
        } catch {
          await navigator.share({ text, url: publicUrl });
          return;
        }
      } catch { /* fall through to whatsapp */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="smart-invite-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Wand2 size={18} className="text-[#E83E8C]" />
            <h3 className="font-bold text-[#1E1B4B]">
              {step === 1 ? "Create Smart Invite" : "Invite Ready!"}
            </h3>
          </div>
          <button onClick={close} className="p-1 hover:bg-gray-100 rounded-full" data-testid="smart-invite-close">
            <X size={18} className="text-[#94a3b8]" />
          </button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-4">
            <p className="text-xs text-[#475569]">
              AI will generate a beautiful invite image for <span className="font-semibold text-[#1E1B4B]">{defaults.occasion || "your party"}</span>.
              Your guests RSVP with one tap.
            </p>

            {/* Style picker */}
            <div>
              <label className="block text-xs font-semibold text-[#1E1B4B] mb-2">Invitation style</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setStyle("surprise")}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    style === "surprise" ? "border-[#E83E8C] bg-[#E83E8C]/5" : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid="invite-style-surprise"
                >
                  <div className="flex items-center gap-1 text-xs font-bold text-[#E83E8C] mb-1">
                    <Sparkles size={12} /> Surprise
                  </div>
                  <p className="text-[10px] text-[#475569]">Fresh invite-aesthetic image. Room stays a surprise.</p>
                </button>
                <button
                  onClick={() => canReveal && setStyle("reveal")}
                  disabled={!canReveal}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    style === "reveal" ? "border-[#E83E8C] bg-[#E83E8C]/5" : "border-gray-200 hover:border-gray-300"
                  } ${!canReveal ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid="invite-style-reveal"
                >
                  <div className="flex items-center gap-1 text-xs font-bold text-[#8b5cf6] mb-1">
                    <Sparkles size={12} /> Reveal
                  </div>
                  <p className="text-[10px] text-[#475569]">
                    {canReveal ? "Uses your Magic room preview as background." : "Needs a Magic preview or booking."}
                  </p>
                </button>
              </div>
            </div>

            <input
              value={hostName}
              onChange={e => setHostName(e.target.value)}
              placeholder="Host name(s) — e.g. Ananya & Rohan"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
              data-testid="invite-host-name"
            />

            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                data-testid="invite-event-date"
              />
              <input
                value={eventTime}
                onChange={e => setEventTime(e.target.value)}
                placeholder="Time — 7:00 PM"
                className="px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                data-testid="invite-event-time"
              />
            </div>

            <input
              value={venue}
              onChange={e => setVenue(e.target.value)}
              placeholder="Venue — e.g. 42 Pearl Apartments, Bandra"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
              data-testid="invite-venue"
            />

            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Personal note (optional) — Can't wait to celebrate with you!"
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm resize-none"
              data-testid="invite-note"
            />

            {error && <p className="text-xs text-rose-500">{error}</p>}

            <button
              onClick={generate}
              disabled={generating}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="invite-generate-btn"
            >
              {generating ? <><Loader2 size={16} className="animate-spin" /> Generating magic invite...</> : <><Wand2 size={16} /> Generate AI Invite</>}
            </button>
          </div>
        )}

        {step === 2 && result && (
          <div className="p-6 space-y-4">
            <div className="rounded-2xl overflow-hidden shadow-md border border-gray-100">
              <AIImageWithDisclaimer
                src={`data:image/jpeg;base64,${result.image_base64}`}
                alt="Your invite"
                className="w-full block"
                data-testid="invite-preview-image"
              />
            </div>
            <p className="text-xs text-center text-[#475569]">
              Share this anywhere. Every guest who opens it can RSVP with one tap.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={nativeShare}
                className="py-3 rounded-xl bg-gradient-to-r from-[#25D366] to-[#1da851] text-white font-bold text-xs flex items-center justify-center gap-2"
                data-testid="invite-share-btn"
              >
                <Share2 size={14} /> Share
              </button>
              <button
                onClick={copyUrl}
                className={`py-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                  copied ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "border-[#E83E8C]/30 text-[#E83E8C] hover:bg-[#E83E8C]/5"
                }`}
                data-testid="invite-copy-link-btn"
              >
                {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Link</>}
              </button>
            </div>
            <p className="text-[10px] text-center text-[#94a3b8] break-all">{publicUrl}</p>
          </div>
        )}
      </div>
    </div>
  );
}
